import {Button} from '@dabafinance/react-components';
import {DatePicker, Form, Input, Pagination, Select, Table, Tag} from 'antd';
import moment from 'moment';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {
  GET_NOTIFICATION_BUCKETS,
  GET_NOTIFICATION_WORKFLOWS,
  GET_ORCHESTRATOR_NOTIFICATIONS,
} from 'graphql/queries/notificationOrchestrator';
import {getUserTimezone} from 'utils/helper';
import styles from './workflowOrchestrator.module.scss';

const STATUS_OPTIONS = [
  {value: 'PENDING', label: 'Pending'},
  {value: 'SENT', label: 'Sent'},
  {value: 'FAILED', label: 'Failed'},
];

const STATUS_COLORS = {
  PENDING: 'yellow',
  SENT: 'green',
  FAILED: 'red',
};

const buildInput = ({
  page,
  limit,
  bucketKey,
  workflowKey,
  startDate,
  endDate,
  search,
}) => {
  const input = {
    page,
    limit,
    status: status || 'PENDING',
    viaOrchestrator: true,
  };
  if (bucketKey) input.bucketKey = bucketKey;
  if (workflowKey) input.workflowKey = workflowKey;
  if (startDate)
    input.startDate = moment(startDate).startOf('day').toISOString();
  if (endDate) input.endDate = moment(endDate).endOf('day').toISOString();
  if (search?.trim()) input.search = search.trim();
  return input;
};

const Notifications = () => {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState('PENDING');
  const [bucketKey, setBucketKey] = useState();
  const [workflowKey, setWorkflowKey] = useState();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [search, setSearch] = useState('');

  const {data: bucketsData} = useQuery(GET_NOTIFICATION_BUCKETS, {
    variables: {input: {}},
    fetchPolicy: 'cache-first',
    onError: error => toast.error(error.message),
  });

  const {data: workflowsData} = useQuery(GET_NOTIFICATION_WORKFLOWS, {
    variables: {input: {}},
    fetchPolicy: 'cache-first',
    onError: error => toast.error(error.message),
  });

  const {data, loading, refetch} = useQuery(GET_ORCHESTRATOR_NOTIFICATIONS, {
    variables: {
      input: buildInput({
        page,
        limit,
        status,
        bucketKey,
        workflowKey,
        startDate,
        endDate,
        search,
      }),
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error.message),
  });

  const buckets = bucketsData?.getNotificationBuckets?.data || [];
  const workflows = workflowsData?.getNotificationWorkflows?.data || [];

  const rows = data?.getScheduledNotifications?.data || [];
  const total = data?.getScheduledNotifications?.pagination?.total || 0;

  const applyFilters = () => {
    setPage(1);
    refetch({
      input: buildInput({
        page: 1,
        limit,
        status,
        bucketKey,
        workflowKey,
        startDate,
        endDate,
        search,
      }),
    });
  };

  const resetFilters = () => {
    setStatus('PENDING');
    setBucketKey(undefined);
    setWorkflowKey(undefined);
    setStartDate(null);
    setEndDate(null);
    setSearch('');
    setPage(1);
    refetch({input: buildInput({page: 1, limit, status: 'PENDING'})});
  };

  const handlePageChange = (nextPage, nextLimit) => {
    setPage(nextPage);
    setLimit(nextLimit);
    refetch({
      input: buildInput({
        page: nextPage,
        limit: nextLimit,
        status,
        bucketKey,
        workflowKey,
        startDate,
        endDate,
        search,
      }),
    });
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: title => title || <span style={{color: '#8b82a7'}}>—</span>,
    },
    {
      title: 'Bucket',
      dataIndex: 'bucket',
      key: 'bucket',
      render: value =>
        value ? <Tag color="purple">{value}</Tag> : <span>—</span>,
    },
    {
      title: 'Workflow',
      dataIndex: 'workflowKey',
      key: 'workflowKey',
      render: value =>
        value ? <Tag color="blue">{value}</Tag> : <span>—</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: value =>
        value ? (
          <Tag color={STATUS_COLORS[value] || 'default'}>{value}</Tag>
        ) : (
          <span>—</span>
        ),
    },
    {
      title: 'Scheduled',
      dataIndex: 'schedule',
      key: 'schedule',
      render: (_, record) =>
        record?.schedule?.date ? (
          <div>
            <div>{moment(record.schedule.date).format('LLL')}</div>
            <small style={{color: '#8b82a7'}}>{getUserTimezone()}</small>
          </div>
        ) : (
          <span>—</span>
        ),
    },
    {
      title: 'Actions',
      dataIndex: 'operation',
      key: 'actions',
      render: (_, record) => (
        <Button
          label="View"
          type="secondary"
          onClick={() =>
            navigate(`/dashboard/notification/update/${record.id}`)
          }
        />
      ),
    },
  ];

  return (
    <div>
      <div className={styles.filters}>
        <Select
          placeholder="Status"
          value={status}
          onChange={setStatus}
          style={{width: 160}}>
          {STATUS_OPTIONS.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
        <Select
          allowClear
          placeholder="Bucket"
          value={bucketKey}
          onChange={setBucketKey}
          style={{width: 200}}
          showSearch
          optionFilterProp="children">
          {buckets.map(b => (
            <Select.Option key={b.key} value={b.key}>
              {b.name}
            </Select.Option>
          ))}
        </Select>
        <Select
          allowClear
          placeholder="Workflow"
          value={workflowKey}
          onChange={setWorkflowKey}
          style={{width: 220}}
          showSearch
          optionFilterProp="children">
          {workflows.map(w => (
            <Select.Option key={w.workflowKey} value={w.workflowKey}>
              {w.name || w.workflowKey}
            </Select.Option>
          ))}
        </Select>
        <Form.Item style={{margin: 0}} label="Start date">
          <DatePicker
            allowClear
            value={startDate}
            onChange={setStartDate}
            style={{width: 180}}
          />
        </Form.Item>
        <Form.Item style={{margin: 0}} label="End date">
          <DatePicker
            allowClear
            value={endDate}
            onChange={setEndDate}
            style={{width: 180}}
          />
        </Form.Item>
        <Input
          allowClear
          placeholder="Search title"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{width: 200}}
        />
        <Button
          label="Apply"
          className={styles['button-style']}
          onClick={applyFilters}
        />
        <Button label="Reset" type="secondary" onClick={resetFilters} />
      </div>

      <div className={styles.body}>
        <Table
          rowKey={record => record.id}
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={false}
        />
        <Pagination
          current={page}
          onChange={handlePageChange}
          total={total}
          showQuickJumper
          className="ant-table-pagination"
          showSizeChanger
          pageSize={limit}
          style={{marginTop: 16, textAlign: 'right'}}
        />
      </div>
    </div>
  );
};

export default Notifications;
